//* 운영 계정(Admin·정산 관리자) 로그인 2단계 인증 — 비밀번호 다음 단계 화면
import { FC, useCallback, useEffect, useState } from 'react';

import { BugoBasicButton } from '@service/bugo/components/BugoBasicButton';
import {
  LoginPhoneChallenge,
  confirmLoginPhoneCode,
  sendLoginPhoneCode,
} from '@shared/api/auth/loginPhoneVerification.controller';
import { usePhoneCodeVerification } from '@shared/hooks/usePhoneCodeVerification';
import { formatMmSs } from '@shared/utils/timeFormat.util';

interface Props {
  challenge: LoginPhoneChallenge;
  /** 인증 성공 → 발급된 로그인 토큰 */
  onVerified: (token: string) => void;
  /** 처음(아이디·비밀번호)으로 돌아가기 */
  onCancel: () => void;
}

//* 인증번호 재발송 간격
const RESEND_COOLDOWN_SEC = 30;

/**
 * 서버가 "이 인증번호로는 더 진행할 수 없다"고 알리는 문장들.
 * 이 경우 입력을 고쳐도 소용없고 재발송만이 길이라 코드 입력칸을 걷어낸다.
 */
const SPENT_VERIFICATION_HINTS = ['이미 인증', '만료', '시도 횟수'];

const isSpentVerification = (message: string): boolean =>
  SPENT_VERIFICATION_HINTS.some((hint) => message.includes(hint));

/**
 * 비밀번호는 맞았고 토큰은 아직 없는 상태. 등록된 번호로 6자리를 받아 확인하면 그때 토큰이 나온다.
 * challenge 는 10분짜리라 만료되면 로그인부터 다시 해야 한다.
 *
 * 모달이 아니라 로그인 폼 자리를 차지하는 화면으로 둔다 — 뒤로가기·스크롤 잠금 문제를 만들지 않는다.
 */
export const LoginPhoneVerification: FC<Props> = function LoginPhoneVerification({
  challenge,
  onVerified,
  onCancel,
}: Props) {
  const [challengeSeconds, setChallengeSeconds] = useState(challenge.expiresInSec);

  //* challenge(10분) 만료 카운트다운 — 만료되면 로그인부터 다시
  useEffect(() => {
    const deadline = Date.now() + challenge.expiresInSec * 1000;
    setChallengeSeconds(challenge.expiresInSec);
    const timer = setInterval(() => {
      setChallengeSeconds(Math.max(0, Math.round((deadline - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(timer);
  }, [challenge.expiresInSec, challenge.challenge]);

  const challengeExpired = challengeSeconds <= 0;

  const {
    verificationId,
    code,
    setCode,
    sending,
    confirming,
    error,
    setError,
    cooldown,
    codeSeconds,
    handleSend: sendCode,
    handleConfirm,
    reset,
  } = usePhoneCodeVerification({
    resendCooldownSec: RESEND_COOLDOWN_SEC,
    send: () => sendLoginPhoneCode(challenge.challenge),
    confirm: ({ verificationId: id, code: value }) =>
      confirmLoginPhoneCode({ challenge: challenge.challenge, verificationId: id, code: value }),
    onVerified: (result) => onVerified(result.token),
    onConfirmError: (_err, message) => {
      if (!isSpentVerification(message)) return false;
      //* 소진된 인증번호 — 재발송을 바로 누를 수 있게 되돌린다
      reset();
      setError(`${message} 인증번호를 다시 받아주세요.`);
      return true;
    },
  });

  //* challenge 가 만료됐으면 발송 자체를 막는다
  const handleSend = useCallback(async () => {
    if (challengeExpired) return;
    await sendCode();
  }, [challengeExpired, sendCode]);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-16 font-bold theme-text-1">휴대폰 인증</h3>
        <p className="mt-1 text-14 leading-5 text-gray-500">
          관리자 계정은 등록된 휴대폰으로 본인 확인 후 로그인됩니다.
          <br />
          인증하면 이 기기에서는 30일간 다시 묻지 않습니다.
        </p>
      </div>

      <div className="rounded-lg bg-[#F9FAFB] px-4 py-3">
        <p className="text-14 theme-text-1">
          <span className="text-gray-500">인증번호 받을 번호</span>
          <span className="ml-2 font-bold">{challenge.phoneMasked}</span>
        </p>
      </div>

      {challengeExpired ? (
        <p className="text-14 text-red-500">
          인증 가능 시간이 지났습니다. 로그인부터 다시 진행해 주세요.
        </p>
      ) : (
        <>
          <BugoBasicButton
            type="button"
            colorTheme={verificationId ? 'default' : 'primary'}
            className="rounded-lg disabled:opacity-40"
            onClick={handleSend}
            disabled={sending || cooldown > 0}
            text={
              sending
                ? '발송 중...'
                : cooldown > 0
                ? `재발송 (${cooldown}초)`
                : verificationId
                ? '인증번호 재발송'
                : '인증번호 받기'
            }
          />

          {verificationId && (
            <div className="space-y-2">
              {/* 자간은 입력된 숫자에만 준다 — placeholder까지 벌어지면 글자가 흩어져 보인다 */}
              <div className="relative">
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6));
                    setError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleConfirm();
                    }
                  }}
                  placeholder="인증번호 6자리"
                  className={`h-11 w-full rounded-lg border border-gray-300 bg-[#F9FAFB] px-14 text-center text-15 font-bold theme-bg-1 placeholder:font-normal placeholder:text-gray-400 ${
                    code ? 'tracking-[0.4em] indent-[0.4em]' : ''
                  }`}
                />
                {codeSeconds > 0 && (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-14 tabular-nums text-red-500">
                    {formatMmSs(codeSeconds)}
                  </span>
                )}
              </div>
              <BugoBasicButton
                type="button"
                className="rounded-lg theme-bg-main disabled:opacity-40"
                onClick={handleConfirm}
                disabled={confirming || code.length !== 6}
                text={confirming ? '확인 중...' : '인증 확인'}
              />
            </div>
          )}
        </>
      )}

      {error && <p className="text-14 text-red-500">{error}</p>}

      <button
        type="button"
        className="w-full py-1 text-14 text-gray-500 underline"
        onClick={onCancel}
      >
        다른 계정으로 로그인
      </button>
    </div>
  );
};
